import { CognitoIdentityProviderClient, InitiateAuthCommand } from "@aws-sdk/client-cognito-identity-provider";
import crypto from "crypto-js";
import Cookies from "js-cookie";
import type { IMatchSummary } from "../views/leaguesHome/tournaments/tournamentsDetails/matches/types/IMatches";
import type { IMatchDetails, InningScorecard, ITeamsSquad, IBattingScoreCardItem } from "../views/leaguesHome/tournaments/tournamentsDetails/matches/matchesDetails/types/matchDetails.d";

//12 aug - 30 sept (will convert in this format)
export const formatDateRange = (
  startISO?: string,
  endISO?: string,
  timeZone = "Asia/Kolkata",
  locale: string = "en-GB"
): string => {
  if (!startISO || !endISO) return "-";
  const s = new Date(startISO);
  const e = new Date(endISO);
  if (isNaN(s.getTime()) || isNaN(e.getTime())) return "-";
  const fmt = (d: Date, opts: Intl.DateTimeFormatOptions) =>
    new Intl.DateTimeFormat(locale, { timeZone, ...opts }).format(d);
  const sameYear = fmt(s, { year: "numeric" }) === fmt(e, { year: "numeric" });
  const sameMonth = sameYear && fmt(s, { month: "short" }) === fmt(e, { month: "short" });
  const dayMon = (d: Date) => `${fmt(d, { day: "numeric" })} ${fmt(d, { month: "short" })}`;
  if (sameYear && sameMonth) {
    return `${dayMon(s)} - ${dayMon(e)}, ${fmt(s, { year: "numeric" })}`;
  }
  if (sameYear) {
    return `${dayMon(s)} - ${dayMon(e)}, ${fmt(s, { year: "numeric" })}`;
  }
  const dayMonYear = (d: Date) => fmt(d, { day: "numeric", month: "short", year: "numeric" });
  return `${dayMonYear(s)} - ${dayMonYear(e)}`;
};

/** API enums like PLAYER_UNCLAIMED / LEFT_HANDED → "Player Unclaimed" / "Left Handed" for display. */
export const formatBackendEnumLabel = (value: string | null | undefined): string => {
  const s = (value ?? "").trim();
  if (!s || s === "-") return "-";
  return s
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

/** Role labels for UI: PLAYER_UNCLAIMED → "Player"; other roles use formatBackendEnumLabel. */
export const formatPlayerRoleLabel = (value: string | null | undefined): string => {
  const s = (value ?? "").trim();
  if (!s || s === "-") return "-";
  const compact = s.toUpperCase().replace(/[^A-Z]/g, "");
  if (compact === "PLAYERUNCLAIMED") return "Player";
  return formatBackendEnumLabel(s);
};

/**
 * Calendar-day status using both start and end. A multi-day tournament stays
 * "Ongoing" until the day after its end date (same local calendar as `today`).
 */
export const getTournamentStatus = (startDate: string, endDate: string) => {
  const now = new Date();
  const start = new Date(startDate);
  if (Number.isNaN(start.getTime())) return "Upcoming";

  const endParsed = endDate && String(endDate).trim() ? new Date(endDate) : start;
  const end = Number.isNaN(endParsed.getTime()) ? start : endParsed;

  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startDateOnly = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const endDateOnly = new Date(end.getFullYear(), end.getMonth(), end.getDate());

  if (startDateOnly > today) return "Upcoming";
  if (endDateOnly < today) return "Completed";
  return "Ongoing";
};


// "FEBRUARY, SAT 15, 7:30 PM"(will convert in this format)
export function formatDateAndTime(startDate: string) {
  const date = new Date(startDate);
  const months = [
    "JANUARY", "FEBRUARY", "MARCH", "APRIL", "MAY", "JUNE",
    "JULY", "AUGUST", "SEPTEMBER", "OCTOBER", "NOVEMBER", "DECEMBER"
  ];
  const weekdays = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];

  const month = months[date.getMonth()];
  const dayOfWeek = weekdays[date.getDay()];
  const day = String(date.getDate()).padStart(2, "0");

  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";

  hours = hours % 12;
  hours = hours ? hours : 12;

  return `${month}, ${dayOfWeek} ${day}, ${hours}:${minutes} ${ampm}`;
}


// Format date from "14-NOV 2025" to "14 Nov 2025"
export const formatDate = (dateString: string): string => {
  const parts = dateString.split(' ');
  if (parts.length >= 2) {
    const datePart = parts[0]; // "14-NOV"
    const yearPart = parts[1]; // "2025"
    const [day, month] = datePart.split('-');
    return `${day} ${month.charAt(0)}${month.slice(1).toLowerCase()} ${yearPart}`;
  }
  return dateString;
};


export function formatPrettyDateAndTime(startDate: string) {
  const date = new Date(startDate);
  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];
  const weekdays = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thrusday", "Friday", "Saturday"];

  const month = months[date.getMonth()];
  const dayOfWeek = weekdays[date.getDay()];
  const day = String(date.getDate()).padStart(2, "0");

  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";

  hours = hours % 12;
  hours = hours ? hours : 12;

  return `${dayOfWeek} ${day} ${month}, ${hours}:${minutes} ${ampm}`;
}

export type TimeUntilMatch = {
  days: number;
  hours: number;
  minutes: number;
  isPast: boolean;
  /** True when schedule string is missing or could not be parsed — do not treat as "past". */
  isUnknown?: boolean;
};

/**
 * Parse match schedule strings from the API into a local Date.
 * Handles ISO-8601, "yyyy-MM-dd HH:mm:ss" (space separator), and dd/MM/yyyy variants.
 */
export const parseMatchInstant = (
  raw: string | number | undefined | null,
): Date | null => {
  if (raw == null) return null;
  if (typeof raw === "number" && Number.isFinite(raw)) {
    const d = new Date(raw);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const s = String(raw).trim();
  if (!s) return null;

  let d = new Date(s);
  if (!Number.isNaN(d.getTime())) return d;

  const spaceDate = s.match(
    /^(\d{4})-(\d{2})-(\d{2})[ T](\d{1,2}):(\d{2})(?::(\d{2}))?(?:\.(\d{1,3}))?/,
  );
  if (spaceDate) {
    const y = parseInt(spaceDate[1], 10);
    const mo = parseInt(spaceDate[2], 10) - 1;
    const day = parseInt(spaceDate[3], 10);
    const hh = parseInt(spaceDate[4], 10);
    const mm = parseInt(spaceDate[5], 10);
    const ss = spaceDate[6] != null ? parseInt(spaceDate[6], 10) : 0;
    d = new Date(y, mo, day, hh, mm, ss);
    if (!Number.isNaN(d.getTime())) return d;
  }

  const dmy = s.match(
    /^(\d{1,2})[/-](\d{1,2})[/-](\d{4})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?)?$/,
  );
  if (dmy) {
    const day = parseInt(dmy[1], 10);
    const month = parseInt(dmy[2], 10) - 1;
    const year = parseInt(dmy[3], 10);
    const hh = dmy[4] != null ? parseInt(dmy[4], 10) : 0;
    const mm = dmy[5] != null ? parseInt(dmy[5], 10) : 0;
    const ss = dmy[6] != null ? parseInt(dmy[6], 10) : 0;
    d = new Date(year, month, day, hh, mm, ss);
    if (!Number.isNaN(d.getTime())) return d;
  }

  return null;
};

export const formatTime = (dateString: string | undefined | null) => {
  const date = parseMatchInstant(dateString);
  if (!date) {
    return { time: "—", day: "—" };
  }
  return {
    time: date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    }),
    day: date
      .toLocaleDateString("en-US", { weekday: "short" })
      .toUpperCase(),
  };
};

export const formatMatchDate = (dateString: string | undefined | null) => {
  const date = parseMatchInstant(dateString);
  if (!date) return "—";
  return date
    .toLocaleDateString("en-US", {
      day: "numeric",
      month: "long",
      year: "numeric",
    })
    .toUpperCase();
};

/** "Today" / "Tomorrow" / short date — use instead of hardcoding Today on cards. */
export const formatUpcomingMatchDayLabel = (
  dateString: string | undefined | null,
): string => {
  const matchDate = parseMatchInstant(dateString);
  if (!matchDate) return "—";

  const startOfDay = (x: Date) =>
    new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const d0 = startOfDay(new Date());
  const d1 = startOfDay(matchDate);
  const diffDays = Math.round((d1 - d0) / 86400000);

  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Tomorrow";
  if (diffDays === -1) return "Yesterday";
  return matchDate.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
};

export const getTimeUntilMatch = (
  dateString: string | undefined | null,
): TimeUntilMatch => {
  const matchDate = parseMatchInstant(dateString);
  if (!matchDate) {
    return { days: 0, hours: 0, minutes: 0, isPast: false, isUnknown: true };
  }
  const now = new Date();
  const diffMs = matchDate.getTime() - now.getTime();

  if (diffMs <= 0) {
    return { days: 0, hours: 0, minutes: 0, isPast: true, isUnknown: false };
  }

  const days = Math.floor(diffMs / 86400000);
  const hours = Math.floor((diffMs % 86400000) / 3600000);
  const minutes = Math.floor((diffMs % 3600000) / 60000);

  return { days, hours, minutes, isPast: false, isUnknown: false };
};

export const getTimeUntilMatchDetailed = (
  dateString: string | undefined | null,
) => {
  const matchDate = parseMatchInstant(dateString);
  if (!matchDate) {
    return { days: 0, hours: 0, minutes: 0 };
  }
  const now = new Date();
  const diffMs = matchDate.getTime() - now.getTime();

  if (diffMs <= 0) return { days: 0, hours: 0, minutes: 0 };

  const days = Math.floor(diffMs / 86400000);
  const hours = Math.floor((diffMs % 86400000) / 3600000);
  const minutes = Math.floor((diffMs % 3600000) / 60000);

  return { days, hours, minutes };
};

/** Ground name, address, city for match cards (deduped, case-insensitive). */
export const formatMatchVenueLine = (match: {
  city?: string | null;
  groundId?: {
    groundName?: string | null;
    groundAddress?: string | null;
  } | null;
}): string => {
  const city = match?.city?.trim();
  const g = match?.groundId;
  if (g && typeof g === "object") {
    const name = g.groundName?.trim();
    const addr = g.groundAddress?.trim();
    const raw = [name, addr, city].filter(
      (s): s is string => Boolean(s && s.length > 0),
    );
    const deduped = raw.filter(
      (s, i) =>
        raw.findIndex((x) => x.toLowerCase() === s.toLowerCase()) === i,
    );
    if (deduped.length > 0) return deduped.join(", ");
  }
  return city || "TBD";
};

/** When match has no kickoff, show tournament window e.g. "Apr 1, 2026 – Apr 3, 2026". */
export const formatTournamentDateWindowShort = (match: {
  tournament?: { startDate?: string; endDate?: string } | null;
}): string | null => {
  const t = match?.tournament;
  if (!t?.startDate || !t?.endDate) return null;
  const ds = parseMatchInstant(t.startDate);
  const de = parseMatchInstant(t.endDate);
  if (!ds || !de) return null;
  const fmt = (d: Date) =>
    d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  return `${fmt(ds)} – ${fmt(de)}`;
};

/** Cognito API is regional; must match the prefix of VITE_COGNITO_USER_POOL_ID (e.g. us-east-1_fromPoolId). */
const cognitoRegion =
  import.meta.env.VITE_COGNITO_USER_POOL_ID?.split('_')[0] ?? 'us-east-2';

export const clientRegion = new CognitoIdentityProviderClient({
  region: cognitoRegion,
});

export const generateSecretHash = (username: any) => {
  const hmac = crypto.HmacSHA256(username + import.meta.env.VITE_COGNITO_CLIENT_ID, import.meta.env.VITE_COGNITO_CLIENT_SECRET_ID);
  return crypto.enc.Base64.stringify(hmac);
};

/** Meets typical Cognito password policy (8+, uppercase, special). Used only to validate forgot-password OTP via ConfirmForgotPassword. */
export function generateForgotFlowTempPassword(): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$%';
  let s = 'Aa1!';
  for (let i = 0; i < 16; i++) {
    s += chars[Math.floor(Math.random() * chars.length)];
  }
  return s;
}

// Secure cookie helper functions
export const setAuthToken = (token: string, keepLoggedIn: boolean = false) => {
  const isProduction = import.meta.env.PROD;
  const cookieOptions = {
    secure: isProduction, // Only send over HTTPS in production
    sameSite: 'strict' as const, // CSRF protection
    expires: keepLoggedIn ? 30 : undefined, // 30 days if keep logged in, otherwise session cookie
  };

  Cookies.set('Authorization', token, cookieOptions);
  // Notify listeners so components like HomeRoute can re-render when token is set
  window.dispatchEvent(new CustomEvent('auth-token-updated'));
};

export const getAuthToken = (): string | undefined => {
  return Cookies.get('Authorization');
};

/** Cognito id_token / refresh_token from social (Hosted UI) sign-in; password flow never sets these. */
export const setIdToken = (token: string, keepLoggedIn: boolean = false) => {
  Cookies.set('IdToken', token, {
    secure: import.meta.env.PROD,
    sameSite: 'strict' as const,
    expires: keepLoggedIn ? 30 : undefined,
  });
};

export const getIdToken = (): string | undefined => Cookies.get('IdToken');

export const setRefreshToken = (token: string, keepLoggedIn: boolean = false) => {
  Cookies.set('RefreshToken', token, {
    secure: import.meta.env.PROD,
    sameSite: 'strict' as const,
    expires: keepLoggedIn ? 30 : undefined,
  });
};

export const getRefreshToken = (): string | undefined => Cookies.get('RefreshToken');

export const removeAuthToken = () => {
  Cookies.remove('Authorization', {
    secure: import.meta.env.PROD,
    sameSite: 'strict',
  });
  Cookies.remove('IdToken', {
    secure: import.meta.env.PROD,
    sameSite: 'strict',
  });
  Cookies.remove('RefreshToken', {
    secure: import.meta.env.PROD,
    sameSite: 'strict',
  });
  clearPremiumAccessCache();
};

export const decodeAuthTokenPayload = (token: string): Record<string, unknown> | null => {
  try {
    const base64Url = token.split('.')[1];
    if (!base64Url) return null;
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => `%${(`00${c.charCodeAt(0).toString(16)}`).slice(-2)}`)
        .join('')
    );
    return JSON.parse(jsonPayload) as Record<string, unknown>;
  } catch {
    return null;
  }
};

/**
 * Resumes a Cognito session using the stored refresh token (REFRESH_TOKEN_AUTH).
 * The Cognito username needed for SECRET_HASH is pulled from the expired access token's
 * own `username` claim (decoding doesn't require the token to still be valid).
 * Concurrent callers share one in-flight request so a burst of 401s only refreshes once.
 * Returns the new access token, or null if there's nothing to refresh with or the
 * refresh token itself has expired/been revoked.
 */
let pendingRefresh: Promise<string | null> | null = null;

const doRefreshAuthSession = async (): Promise<string | null> => {
  try {
    const refreshToken = getRefreshToken();
    const currentAccessToken = getAuthToken();
    const username = currentAccessToken
      ? (decodeAuthTokenPayload(currentAccessToken)?.username as string | undefined)
      : undefined;

    if (!refreshToken || !username) return null;

    const response = await clientRegion.send(
      new InitiateAuthCommand({
        AuthFlow: 'REFRESH_TOKEN_AUTH',
        ClientId: import.meta.env.VITE_COGNITO_CLIENT_ID,
        AuthParameters: {
          REFRESH_TOKEN: refreshToken,
          SECRET_HASH: generateSecretHash(username),
        },
      })
    );

    const newAccessToken = response?.AuthenticationResult?.AccessToken;
    if (!newAccessToken) return null;

    setAuthToken(newAccessToken, true);
    const newIdToken = response?.AuthenticationResult?.IdToken;
    if (newIdToken) setIdToken(newIdToken, true);
    // Cognito only returns a new RefreshToken when rotation is enabled; keep the existing one otherwise.
    const newRefreshToken = response?.AuthenticationResult?.RefreshToken;
    if (newRefreshToken) setRefreshToken(newRefreshToken, true);

    return newAccessToken;
  } catch {
    return null;
  }
};

export const refreshAuthSession = (): Promise<string | null> => {
  if (!pendingRefresh) {
    pendingRefresh = doRefreshAuthSession().finally(() => {
      pendingRefresh = null;
    });
  }
  return pendingRefresh;
};

const normalizeClaimValues = (value: unknown): string[] => {
  if (typeof value === 'string') {
    return value
      .split(',')
      .map((item) => item.trim().toLowerCase())
      .filter(Boolean);
  }

  if (Array.isArray(value)) {
    return value
      .filter((item): item is string => typeof item === 'string')
      .map((item) => item.trim().toLowerCase())
      .filter(Boolean);
  }

  return [];
};

/** Get display name for welcome message - from JWT name, given_name, or email prefix */
export const getUserDisplayName = (token?: string): string => {
  if (!token) return 'User';
  const payload = decodeAuthTokenPayload(token);
  if (!payload) return 'User';
  const name = payload.name ?? payload.given_name ?? payload.preferred_username;
  if (typeof name === 'string' && name.trim()) return name.trim();
  const email = payload.email ?? payload.username ?? payload['cognito:username'];
  if (typeof email === 'string' && email.trim()) {
    const prefix = email.split('@')[0];
    if (prefix) return prefix.charAt(0).toUpperCase() + prefix.slice(1).toLowerCase();
  }
  return 'User';
};

export const hasPremiumAccess = (token?: string): boolean => {
  if (!token) return getPremiumAccessFromStorage();

  const payload = decodeAuthTokenPayload(token);
  if (!payload) return getPremiumAccessFromStorage();

  const truthyPremiumClaims = ['custom:isPremium', 'isPremium', 'premium', 'custom:premium'];
  const premiumKeywords = ['premium', 'elite', 'pro', 'paid', 'subscriber', 'streaming'];
  const positiveStatus = ['active', 'trialing', 'premium', 'paid'];

  for (const claim of truthyPremiumClaims) {
    const value = payload[claim];
    if (typeof value === 'boolean' && value) return true;
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      if (normalized === 'true' || normalized === '1' || premiumKeywords.includes(normalized)) {
        return true;
      }
    }
  }

  const planAndRoleClaims = [
    'custom:plan',
    'plan',
    'subscriptionPlan',
    'custom:subscriptionPlan',
    'membershipType',
    'custom:membershipType',
    'cognito:groups',
    'groups',
    'roles',
  ];

  for (const claim of planAndRoleClaims) {
    const values = normalizeClaimValues(payload[claim]);
    if (values.some((item) => premiumKeywords.some((keyword) => item.includes(keyword)))) {
      return true;
    }
  }

  const statusClaims = ['subscriptionStatus', 'custom:subscriptionStatus', 'membershipStatus'];
  for (const claim of statusClaims) {
    const values = normalizeClaimValues(payload[claim]);
    if (values.some((item) => positiveStatus.includes(item))) {
      return true;
    }
  }

  return getPremiumAccessFromStorage();
};

export const setPremiumAccessInStorage = () => {
  localStorage.setItem('hasPremiumAccess', 'true');
  window.dispatchEvent(new Event('premium-access-updated'));
};

export const getPremiumAccessFromStorage = (): boolean => {
  return localStorage.getItem('hasPremiumAccess') === 'true';
};

export const clearPremiumAccessCache = () => {
  localStorage.removeItem('hasPremiumAccess');
  window.dispatchEvent(new Event('premium-access-updated'));
};

/**
 * Only allow a same-app relative path as a login-redirect target — never an absolute/external URL
 * or a protocol-relative one (`//evil.com`, which browsers resolve to an external host). This
 * value can come straight from a URL query param, i.e. untrusted input, so this is an open-redirect
 * guard, not just a sanity check.
 */
export const isSafeRedirectPath = (path: string | null | undefined): path is string =>
  !!path && path.startsWith('/') && !path.startsWith('//');

/**
 * Builds the `/login` URL carrying the current location as a `redirect` param, for sending an
 * unauthenticated (or session-expired) visitor to log in and land back where they were —
 * e.g. a waiver reminder email link opened while logged out.
 */
export const buildLoginRedirectUrl = (returnPath: string): string =>
  isSafeRedirectPath(returnPath) ? `/login?redirect=${encodeURIComponent(returnPath)}` : '/login';

/**
 * Reads the `redirect` query param set by {@link buildLoginRedirectUrl} off a router location's
 * `search` string, returning a safe same-app path to send the user to once they finish
 * authenticating (login, OTP confirmation, or a fresh signup), or `/` if there isn't one or it
 * isn't safe.
 */
export const resolvePostAuthRedirect = (search: string): string => {
  const redirect = new URLSearchParams(search).get('redirect');
  return isSafeRedirectPath(redirect) ? redirect : '/';
};

const SOCIAL_AUTH_REDIRECT_KEY = 'socialAuthRedirect';

/**
 * Stashes the post-login redirect target before a full-page redirect out to Cognito Hosted UI
 * (Google/Apple sign-in) — neither a URL query param nor React Router `state` survives that round
 * trip (Cognito returns to a fixed `redirect_uri`, not wherever the app started), but sessionStorage
 * does. Read back once via {@link consumeSocialAuthRedirect} in SocialAuthCallback.
 */
export const stashSocialAuthRedirect = (path: string) => {
  try {
    if (isSafeRedirectPath(path)) {
      sessionStorage.setItem(SOCIAL_AUTH_REDIRECT_KEY, path);
    } else {
      sessionStorage.removeItem(SOCIAL_AUTH_REDIRECT_KEY);
    }
  } catch {
    // sessionStorage unavailable (private browsing, storage disabled) — social login just falls
    // back to home; nothing else depends on this succeeding.
  }
};

/** Reads and clears the stashed social-auth redirect target — always consume exactly once per
 *  callback run, whether or not the value ends up used, so a stale value can't leak into a later,
 *  unrelated sign-in. */
export const consumeSocialAuthRedirect = (): string => {
  try {
    const value = sessionStorage.getItem(SOCIAL_AUTH_REDIRECT_KEY);
    sessionStorage.removeItem(SOCIAL_AUTH_REDIRECT_KEY);
    return isSafeRedirectPath(value) ? value : '/';
  } catch {
    return '/';
  }
};

// Guest mode helper functions
export const setGuestModeInStorage = (isGuest: boolean) => {
  if (isGuest) {
    localStorage.setItem('isGuestMode', 'true');
  } else {
    localStorage.removeItem('isGuestMode');
  }
};

export const getGuestModeFromStorage = (): boolean => {
  return localStorage.getItem('isGuestMode') === 'true';
};

export const clearGuestMode = () => {
  localStorage.removeItem('isGuestMode');
};

export const getPlayerImageUrl = (imageUri: string | null | undefined) => {
  const cdnBaseUrl = import.meta.env.VITE_IMAGE_CDN_BASE_URL;
  if (imageUri) {
    if (cdnBaseUrl) {
      // Handle trailing/leading slashes
      const baseUrl = cdnBaseUrl.endsWith('/') ? cdnBaseUrl.slice(0, -1) : cdnBaseUrl;
      const imagePath = imageUri.startsWith('/') ? imageUri : `/${imageUri}`;
      return `${baseUrl}${imagePath}`;
    }
    return imageUri;
  }
};

/** Storage keys and relative paths → full CDN URL; leaves http(s) and data: URLs unchanged. */
export const resolvePublicImageUrl = (
  imageUri: string | null | undefined,
): string => {
  if (imageUri == null || String(imageUri).trim() === '') return '';
  // Strip trailing ".null" that the API emits when the file extension is absent
  let uri = String(imageUri).trim().replace(/\.null$/i, '');
  if (uri === '') return '';
  if (/^(https?:\/\/|data:)/i.test(uri)) return uri;
  const cdnBaseUrl = import.meta.env.VITE_IMAGE_CDN_BASE_URL as string | undefined;
  if (cdnBaseUrl) {
    const base = cdnBaseUrl.replace(/\/$/, '');
    return `${base}/${uri.replace(/^\//, '')}`;
  }
  return uri;
};

/** API may send any of these for the same portrait field. */
export type PlayerPortraitFields = {
  imageUri?: string | null;
  image?: string | null;
  imageUrl?: string | null;
};

export const pickPlayerPortraitUri = (p: PlayerPortraitFields): string | null => {
  for (const x of [p.imageUri, p.image, p.imageUrl]) {
    if (x != null && String(x).trim() !== '') return String(x).trim();
  }
  return null;
};

/** Avatar URL for cards/lists, or '' when none (then use placeholder). Prefer over getPlayerImageUrl when paths may be absolute. */
export const resolvePlayerPortraitUrl = (p: PlayerPortraitFields): string => {
  return resolvePublicImageUrl(pickPlayerPortraitUri(p));
};

export const getSeasonYear = (startDate?: string): string => {
  if (!startDate) return "-";
  try {
    const date = new Date(startDate);
    return date.getFullYear().toString();
  } catch (error) {
    return "-";
  }
};


// Build a per-team score summary from an innings list. Unlike a naive
// scores[0] / scores[1] split, this groups *all* innings by batting team, so
// multi-innings formats (e.g. Test / two-innings-a-side) aggregate correctly
// into a combined score such as "250/8 & 180/4".
const buildTeamScores = (
  innings: InningScorecard[],
  teamOneName: string | undefined,
  teamTwoName: string | undefined
) => {
  if (!innings.length) {
    // No innings data, fallback to team names
    return {
      firstTeamScore: null as InningScorecard | null,
      secondTeamScore: null as InningScorecard | null,
      firstTeamScoreText: null as string | null,
      secondTeamScoreText: null as string | null,
      firstTeamName: teamOneName || "",
      secondTeamName: teamTwoName || "",
      showYetToBat: true,
    };
  }

  // First team is the one that batted first.
  const firstTeamName = innings[0].battingTeamName || teamOneName || "";
  const belongsToFirst = (inn: InningScorecard) => (inn.battingTeamName || teamOneName || "") === firstTeamName;

  const firstTeamInnings = innings.filter(belongsToFirst);
  const secondTeamInnings = innings.filter((inn) => !belongsToFirst(inn));

  const secondTeamName = secondTeamInnings[0]?.battingTeamName || (firstTeamName === teamOneName ? teamTwoName : teamOneName) || "";

  // Combined runs across all of a team's innings. For limited-overs matches a
  // team bats once, so this is just "runs/wicket" — identical to before.
  const scoreText = (list: InningScorecard[]) => (list.length ? list.map((i) => `${i.runs ?? 0}/${i.wicket ?? 0}`).join(" & ") : null);

  return {
    // Latest inning per team — preserves over/ball fields existing markup reads.
    firstTeamScore: firstTeamInnings[firstTeamInnings.length - 1] ?? null,
    secondTeamScore: secondTeamInnings[secondTeamInnings.length - 1] ?? null,
    firstTeamScoreText: scoreText(firstTeamInnings),
    secondTeamScoreText: scoreText(secondTeamInnings),
    firstTeamName,
    secondTeamName,
    // Other team hasn't batted yet.
    showYetToBat: secondTeamInnings.length === 0,
  };
};

// Helper function to get team scores based on battingTeamName for IMatchSummary
export const getTeamScores = (match: IMatchSummary) => buildTeamScores(match?.inningScorecardList ?? [], match?.teamOne?.teamName, match?.teamTwo?.teamName);

/** True when the match type is a Test (multi-day, no fixed overs). */
export const isTestMatch = (matchType?: string | null): boolean => (matchType ?? "").trim().toUpperCase().replace(/\s+/g, "_") === "TEST_MATCH";

// Resolve the "equation / result" line shown on match cards and inside the
// match center. Precedence (highest first):
//   1. matchUpdateString  — a manually-set status string always wins when present.
//   2. ENDED matches      — the match-level equation ("<Team> won by X runs").
//   3. IN_PROGRESS matches — the current (last) inning's live equationRun
//                            ("X runs required from Y balls", lead/trail, etc.),
//                            falling back to the match-level equation.
//   4. Otherwise           — the toss result.
type MatchEquationSource = {
  matchStatus?: string | null;
  matchUpdateString?: string | null;
  equation?: string | null;
  matchResult?: string | null;
  tossResult?: string | null;
  inningScorecardList?: { equationRun?: string | null }[] | null;
};

export const getMatchEquationText = (match: MatchEquationSource | undefined | null): string => {
  const clean = (value?: string | null) => {
    const trimmed = value?.trim();
    return trimmed ? trimmed : null;
  };

  // 1. Highest priority: an explicit match update string.
  const update = clean(match?.matchUpdateString);
  if (update) return update;

  const statusKey = match?.matchStatus?.trim().toUpperCase().replace(/\s+/g, "_");

  // 2. ENDED: equation from the match details.
  if (statusKey === "ENDED" || statusKey === "COMPLETED" || statusKey === "FINISHED") {
    return clean(match?.equation) ?? clean(match?.matchResult) ?? "-";
  }

  // 3. IN_PROGRESS: equation from the current (last) inning response.
  if (statusKey === "IN_PROGRESS") {
    const innings = match?.inningScorecardList ?? [];
    const liveEquation = clean(innings[innings.length - 1]?.equationRun);
    return liveEquation ?? clean(match?.equation) ?? "-";
  }

  // 4. Otherwise: toss result.
  return clean(match?.tossResult) ?? "-";
};

export const didNotBatPlayers = (
  playingSquad: ITeamsSquad[] | undefined,
  battingScoreCardResponseList: IBattingScoreCardItem[] | undefined
): Array<{ playerFirstName: string; playerLastName: string }> => {
  if (!playingSquad?.length) {
    return [];
  }

  if (!battingScoreCardResponseList?.length) {
    return playingSquad.map(({ playerFirstName = "", playerLastName = "" }) => ({
      playerFirstName,
      playerLastName
    }));
  }

  const battedPlayerIds = new Set(
    battingScoreCardResponseList
      .map(({ playerId }) => playerId)
      .filter(Boolean)
  );

  return playingSquad
    .filter(({ playerId }) => playerId && !battedPlayerIds.has(playerId))
    .map(({ playerFirstName = "", playerLastName = "" }) => ({
      playerFirstName,
      playerLastName
    }));
};

// Helper function to get team scores based on battingTeamName for IMatchDetails
export const getTeamScoresFromMatchDetails = (matchSummary: IMatchDetails | undefined, scores: InningScorecard[] | undefined) => buildTeamScores(scores ?? [], matchSummary?.teamOne?.teamName, matchSummary?.teamTwo?.teamName);